import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { apiExportReferralDocument } from '../utils/rmsApi';
import useConfidentialDownload from './useConfidentialDownload';

/**
 * Download one referral document (a form's responses or a pathway's triage
 * decision) as a PDF for EMIS/EIS entry.
 *
 * The server builds the document (a few seconds) and returns a short-lived
 * signed URL served as an attachment, so assigning it downloads the file
 * without leaving the page — and, unlike opening a new tab after an await, is
 * not caught by pop-up blockers.
 *
 * One download runs at a time; `pendingKey` names the document being
 * prepared so its control can show a busy state.
 *
 * A user's first download for a patient shows the confidentiality warning
 * (TDI-484, SOP A.10; once per user per patient, TDI-609): the host must
 * render the returned `confirmDialog` once; the export only runs when the
 * user chooses Continue. Pass the live `patientRecord` so the warning knows
 * whether this user has already seen it for this patient.
 *
 * @param {{ orgId: string, patientRecordId: string, patientRecord?: object|null }} args
 * @returns {{ pendingKey: string|null, downloadForm: Function, downloadDecision: Function, downloadDocument: Function, confirmDownload: Function, confirmDialog: React.ReactElement }}
 */
export default function useReferralDocumentDownload({ orgId, patientRecordId, patientRecord }) {
  const [pendingKey, setPendingKey] = useState(null);
  const inFlight = useRef(false);
  const { requestDownload, confirmDialog } = useConfidentialDownload({ orgId, patientRecordId, patientRecord });

  const fetchAndSave = useCallback(async (key, request) => {
    if (!orgId || !patientRecordId || inFlight.current) return;
    inFlight.current = true;
    setPendingKey(key);
    try {
      const { downloadUrl } = await apiExportReferralDocument({ orgId, patientRecordId, ...request });
      window.location.assign(downloadUrl);
    } catch (err) {
      toast.error(err?.message || 'Could not prepare the download. Try again.');
    } finally {
      inFlight.current = false;
      setPendingKey(null);
    }
  }, [orgId, patientRecordId]);

  const run = useCallback(
    (key, request) => requestDownload(() => fetchAndSave(key, request)),
    [requestDownload, fetchAndSave],
  );

  const downloadForm = useCallback(
    (instanceId) => run(`form:${instanceId}`, { document: 'form', instanceId }),
    [run],
  );

  const downloadDecision = useCallback(
    (pathway) => run(`decision:${pathway || 'referral'}`, { document: 'triageDecision', pathway: pathway || null }),
    [run],
  );

  // Any document by request ({ document, instanceId | role | pathway }), keyed
  // by the caller so its control can show the busy state.
  const downloadDocument = useCallback((key, request) => run(key, request), [run]);

  // Any other download the host fetches itself (an uploaded document's PDF,
  // TDI-612), passed through the same confidentiality warning.
  return {
    pendingKey, downloadForm, downloadDecision, downloadDocument, confirmDownload: requestDownload, confirmDialog,
  };
}
